// The SDK as the page uses it: the wasm module loaded once, and each call typed. Every signed
// byte, intent id, nonce and exact figure is the module's; this file only parses its JSON.

import initWasm, * as wasm from "./wasm/papertrade";
import type { SdkWallet } from "./wasm/papertrade";
import { vault } from "./vault";
import type {
  Accepted,
  Account,
  DepositPlan,
  Deployment,
  KeyState,
  Minimums,
  PapertradeError,
  QueueRank,
  QueueSummary,
  SessionKeyInfo,
  Summary,
  TicketPreview,
  Trade,
  TradingState,
  Valuations,
} from "./types";

export type * from "./types";
export type { SdkWallet };

/** Same-origin prefix for the relayer: the Pages Function in production, Vite's proxy in dev. */
export const RELAYER = "/api";

let loading: Promise<void> | null = null;

/** Loads and starts the module once; every call below waits on it. */
export function loadSdk(): Promise<void> {
  loading ??= initWasm().then(() => wasm.init(RELAYER, vault));
  return loading;
}

const parse = <T>(text: string): T => JSON.parse(text) as T;

async function call<T>(run: () => Promise<string>): Promise<T> {
  await loadSdk();
  return parse<T>(await run());
}

export function isPapertradeError(error: unknown): error is PapertradeError {
  return error instanceof Error && typeof (error as { kind?: unknown }).kind === "string";
}

export type TicketInput = {
  owner: string | null;
  instrumentId: number;
  isLong: boolean;
  margin: string;
  leverage: number;
  useDebt: boolean;
  mark: string | null;
  movesBp: number[];
};

export type OpenInput = {
  owner: string;
  instrumentId: number;
  isLong: boolean;
  margin: string;
  leverage: number;
  useDebt: boolean;
  mark: string | null;
};

// The pure calls need the module loaded; the page boots it before it renders them.
export const pt = {
  version: () => wasm.version(),

  // reads
  tradingState: () => call<TradingState>(() => wasm.tradingState()),
  summary: () => call<Summary>(() => wasm.summary()),
  volumes: () => call<[number, string][]>(() => wasm.volumes()),
  queue: () => call<QueueSummary>(() => wasm.queue()),
  recentTrades: () => call<Trade[]>(() => wasm.recentTrades()),
  queueRank: (owner: string) => call<QueueRank>(() => wasm.queueRank(owner)),
  quote: (assetIndex: number) => call<{ bid: string; ask: string; mid: string }>(() => wasm.quote(assetIndex)),
  depositAddress: async (owner: string) => (await loadSdk(), wasm.depositAddress(owner)),
  pendingReward: async (owner: string) => (await loadSdk(), wasm.pendingReward(owner)),
  winFeeRate: async () => (await loadSdk(), wasm.winFeeRate()),
  streamUrl: (owner: string) => wasm.streamUrl(owner),

  // the account
  observe: (owner: string, data: string) => parse<Account>(wasm.observe(owner, data)),
  account: (owner: string) => {
    const text = wasm.account(owner);
    return text === undefined ? null : parse<Account>(text);
  },
  forget: (owner: string) => wasm.forget(owner),
  keyState: async (owner: string) => (await loadSdk(), (await wasm.keyState(owner)) as KeyState),
  sessionKey: async (owner: string) => {
    await loadSdk();
    const text = await wasm.sessionKey(owner);
    return text === undefined ? null : parse<SessionKeyInfo>(text);
  },

  // figures
  parseUnits: (text: string, decimals: number) => wasm.parseUnits(text, decimals),
  formatUnits: (raw: string, decimals: number) => wasm.formatUnits(raw, decimals),
  midOf: (bid: string, ask: string, scale: string) => wasm.midOf(bid, ask, scale) ?? null,
  valuations: (owner: string, marks: Record<string, string>) =>
    parse<Valuations | null>(wasm.valuations(JSON.stringify({ owner, marks }))),
  ticket: (input: TicketInput) => parse<TicketPreview>(wasm.ticket(JSON.stringify(input))),
  mintCurve: (rate: string, decay: string, points: number, span: number) =>
    parse<[string, string][]>(wasm.mintCurve(rate, decay, points, span)),
  paperForLoss: (loss: string, rate: string) => wasm.paperForLoss(loss, rate),
  minimums: () => parse<Minimums>(wasm.minimums()),
  deployment: () => parse<Deployment>(wasm.deployment()),

  // writes
  enable: async (wallet: SdkWallet) => (await loadSdk(), (await wasm.enable(wallet)) as KeyState),
  dropSession: async (owner: string) => (await loadSdk(), wasm.dropSession(owner)),
  open: (input: OpenInput) => call<Accepted>(() => wasm.open(JSON.stringify(input))),
  close: (owner: string, ids: string[]) => call<Accepted[]>(() => wasm.close(owner, JSON.stringify(ids))),
  cancel: (owner: string, intentId: string) => call<Accepted>(() => wasm.cancel(owner, intentId)),
  withdraw: (wallet: SdkWallet, usdc: string) => call<Accepted>(() => wasm.withdraw(wallet, usdc)),
  stake: (wallet: SdkWallet, amount: string) => call<Accepted>(() => wasm.stake(wallet, amount)),
  unstake: (wallet: SdkWallet, amount: string) => call<Accepted>(() => wasm.unstake(wallet, amount)),
  claim: (wallet: SdkWallet) => call<Accepted>(() => wasm.claim(wallet)),
  planDeposit: (owner: string, usdc: string, source: "spot" | "perp", mode: "standard" | "pooled") =>
    call<DepositPlan>(() => wasm.planDeposit(owner, usdc, source, mode)),
  sendDeposit: async (wallet: SdkWallet) => (await loadSdk(), wasm.sendDeposit(wallet)),
};
